import { readStoredToken } from './authApi';

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').trim();

if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL must be configured for the frontend build');
}

function getToken() {
  return readStoredToken();
}

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getToken()}`,
  };
}

async function handleResponse(res) {
  let data = null;
  try {
    data = await res.json();
  } catch {
    /* ignore non-JSON bodies */
  }
  if (!res.ok) {
    throw new Error(data?.detail || `Request failed (${res.status})`);
  }
  return data;
}

export async function getSupportMetadata() {
  const res = await fetch(`${API_BASE_URL}/support/metadata`);
  return handleResponse(res);
}

export async function uploadSupportAttachment(file) {
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${API_BASE_URL}/support/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    body: formData,
  });
  return handleResponse(res);
}

export async function submitTicket({
  subject,
  category,
  message,
  priority = 'medium',
  attachmentUrl = null,
}) {
  const res = await fetch(`${API_BASE_URL}/support/tickets`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      subject,
      category,
      message,
      priority,
      attachment_url: attachmentUrl,
    }),
  });
  return handleResponse(res);
}

export async function getMyTickets() {
  const res = await fetch(`${API_BASE_URL}/support/tickets`, {
    headers: authHeaders(),
  });
  return handleResponse(res);
}

export async function getTicketDetail(ticketId) {
  const res = await fetch(`${API_BASE_URL}/support/tickets/${encodeURIComponent(ticketId)}`, {
    headers: authHeaders(),
  });
  return handleResponse(res);
}

export async function replyToTicket(ticketId, message, attachmentUrl = null) {
  const res = await fetch(`${API_BASE_URL}/support/tickets/${encodeURIComponent(ticketId)}/reply`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ message, attachment_url: attachmentUrl }),
  });
  return handleResponse(res);
}

export async function adminGetTickets({ status, category, priority } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (category) params.set('category', category);
  if (priority) params.set('priority', priority);
  const qs = params.toString();
  const res = await fetch(
    `${API_BASE_URL}/support/admin/tickets${qs ? `?${qs}` : ''}`,
    { headers: authHeaders() },
  );
  return handleResponse(res);
}

export async function adminGetTicketDetail(ticketId) {
  const res = await fetch(
    `${API_BASE_URL}/support/admin/tickets/${encodeURIComponent(ticketId)}`,
    { headers: authHeaders() },
  );
  return handleResponse(res);
}

export async function adminReplyToTicket(ticketId, message) {
  const res = await fetch(
    `${API_BASE_URL}/support/admin/tickets/${encodeURIComponent(ticketId)}/reply`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ message }),
    },
  );
  return handleResponse(res);
}

export async function adminUpdateStatus(ticketId, status) {
  const res = await fetch(
    `${API_BASE_URL}/support/admin/tickets/${encodeURIComponent(ticketId)}/status`,
    {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ status }),
    },
  );
  return handleResponse(res);
}

export async function adminUpdatePriority(ticketId, priority) {
  const res = await fetch(
    `${API_BASE_URL}/support/admin/tickets/${encodeURIComponent(ticketId)}/priority`,
    {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ priority }),
    },
  );
  return handleResponse(res);
}

const supportApi = {
  getSupportMetadata,
  uploadSupportAttachment,
  submitTicket,
  getMyTickets,
  getTicketDetail,
  replyToTicket,
  adminGetTickets,
  adminGetTicketDetail,
  adminReplyToTicket,
  adminUpdateStatus,
  adminUpdatePriority,
};

export default supportApi;
